'use client';

import { useMemo, useState } from 'react';

import { ChatContextButton } from '@/features/chat/components';
import { useGetDeliverableTypesQuery } from '@/features/deliverable-types/api/deliverable-typesApi';
import {
  useCreateJobDeliverablePoolMutation,
  usePatchJobDeliverableMutation,
  usePatchJobDeliverablePoolMutation,
} from '@/features/jobs/api/jobsApi';
import { useGetServiceTypesQuery } from '@/features/service-types/api/service-typesApi';
import {
  Badge,
  ChevronIcon,
  Meter,
  SecondaryButton,
  SmartInput,
} from '@web/ui';
import { formatHours, getErrorMessage, notify } from '@web/utils';

import { DeliverableMetricsStrip } from './DeliverableMetricsStrip';
import { DeliverableTasksTable } from './DeliverableTasksTable';
import styles from './JobTasksComponents.module.scss';

import type { JobDeliverable } from '@/features/jobs/types';
import type { JobKind } from '@/features/jobs/types';

type DeliverableCardProps = {
  jobId: string;
  jobKind: JobKind;
  deliverable: JobDeliverable;
  serviceTypeIds: string[];
  retainerCycleId?: string | null;
  readOnly: boolean;
  initiallyExpanded?: boolean;
  onPatched?: (patch: Partial<JobDeliverable>) => void;
};

export function DeliverableCard({
  jobId,
  jobKind,
  deliverable,
  serviceTypeIds,
  retainerCycleId = null,
  readOnly,
  initiallyExpanded = false,
  onPatched,
}: DeliverableCardProps) {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [isHybridDetailsExpanded, setIsHybridDetailsExpanded] = useState(false);
  const [patchDeliverable, { isLoading }] = usePatchJobDeliverableMutation();
  const [createPool] = useCreateJobDeliverablePoolMutation();
  const [patchPool] = usePatchJobDeliverablePoolMutation();
  const { data: deliverableTypesResponse } = useGetDeliverableTypesQuery({
    page_size: 100,
    show_archived: false,
  });
  const { data: serviceTypesResponse } = useGetServiceTypesQuery({
    page_size: 100,
    show_archived: false,
  });
  const [poolDrafts, setPoolDrafts] = useState<
    Record<string, { description: string; hours: number }>
  >({});

  const serviceTypeLabels = useMemo(
    () =>
      new Map(
        (serviceTypesResponse?.data?.results ?? []).map((serviceType) => [
          serviceType.id,
          serviceType.label,
        ]),
      ),
    [serviceTypesResponse?.data?.results],
  );
  const deliverableTypeLabels = useMemo(
    () =>
      new Map(
        (deliverableTypesResponse?.data?.results ?? []).map(
          (deliverableType) => [deliverableType.id, deliverableType.label],
        ),
      ),
    [deliverableTypesResponse?.data?.results],
  );
  const selectedDeliverableType = useMemo(() => {
    if (!deliverable.deliverable_type_id) {
      return null;
    }

    return {
      id: deliverable.deliverable_type_id,
      label:
        deliverable.deliverable_type_label ??
        deliverableTypeLabels.get(deliverable.deliverable_type_id) ??
        deliverable.deliverable_type_id,
    };
  }, [
    deliverable.deliverable_type_id,
    deliverable.deliverable_type_label,
    deliverableTypeLabels,
  ]);

  const hybridRows = useMemo(() => {
    const seen = new Set<string>();
    const ids: string[] = [];

    for (const id of serviceTypeIds) {
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }

    for (const pool of deliverable.service_type_pools ?? []) {
      const serviceTypeId = pool.service_type?.id ?? pool.service_type_id;
      if (!serviceTypeId || seen.has(serviceTypeId)) {
        continue;
      }
      seen.add(serviceTypeId);
      ids.push(serviceTypeId);
    }

    return ids.map((id) => {
      const pool = (deliverable.service_type_pools ?? []).find(
        (candidate) =>
          (candidate.service_type?.id ?? candidate.service_type_id) === id,
      );
      return {
        id,
        label: serviceTypeLabels.get(id) ?? pool?.service_type?.label ?? id,
        pool: pool ?? null,
      };
    });
  }, [deliverable.service_type_pools, serviceTypeIds, serviceTypeLabels]);

  const getPoolDraft = (
    serviceTypeId: string,
    pool?: {
      hybrid_delivery_description?: string | null;
      estimated_hours?: number | null;
    } | null,
  ) => {
    const draft = poolDrafts[serviceTypeId];
    return {
      description:
        draft?.description ?? pool?.hybrid_delivery_description ?? '',
      hours: draft?.hours ?? pool?.estimated_hours ?? 0,
    };
  };
  const totalHybridHours = hybridRows.reduce(
    (sum, row) => sum + getPoolDraft(row.id, row.pool).hours,
    0,
  );

  const handleHybridToggle = async (checked: boolean) => {
    if (readOnly) {
      return;
    }

    const previousValue = Boolean(deliverable.has_hybrid_delivery);
    onPatched?.({ has_hybrid_delivery: checked });

    try {
      const response = await patchDeliverable({
        jobId,
        deliverableId: deliverable.id,
        payload: { has_hybrid_delivery: checked },
      }).unwrap();
      if (response.data) {
        onPatched?.(response.data);
      }
    } catch (error) {
      onPatched?.({ has_hybrid_delivery: previousValue });
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const handleDeliverableTypeChange = async (
    nextType: { id: string; label: string } | null,
  ) => {
    if (readOnly) {
      return;
    }

    const nextTypeId = nextType?.id ?? null;
    if ((deliverable.deliverable_type_id ?? null) === nextTypeId) {
      return;
    }
    const previousTypeId = deliverable.deliverable_type_id ?? null;
    const previousTypeLabel = deliverable.deliverable_type_label ?? null;

    onPatched?.({
      deliverable_type_id: nextTypeId,
      deliverable_type_label: nextType?.label ?? null,
    });

    try {
      const response = await patchDeliverable({
        jobId,
        deliverableId: deliverable.id,
        payload: { deliverable_type_id: nextTypeId },
      }).unwrap();

      if (response.data) {
        onPatched?.({
          ...response.data,
          deliverable_type_label:
            response.data.deliverable_type_label ?? nextType?.label ?? null,
        });
      } else {
        onPatched?.({
          deliverable_type_id: nextTypeId,
          deliverable_type_label: nextType?.label ?? null,
        });
      }
    } catch (error) {
      onPatched?.({
        deliverable_type_id: previousTypeId,
        deliverable_type_label: previousTypeLabel,
      });
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const handleSavePool = async (serviceTypeId: string) => {
    if (readOnly) {
      return;
    }

    const existingPool = (deliverable.service_type_pools ?? []).find(
      (pool) =>
        (pool.service_type?.id ?? pool.service_type_id) === serviceTypeId,
    );
    const row = hybridRows.find((candidate) => candidate.id === serviceTypeId);
    const draft = getPoolDraft(serviceTypeId, row?.pool ?? null);

    try {
      let nextPool = existingPool;
      if (!existingPool) {
        const created = await createPool({
          deliverableId: deliverable.id,
          payload: {
            service_type_id: serviceTypeId,
            estimated_hours: draft.hours,
            hybrid_delivery_description: draft.description || null,
          },
        }).unwrap();
        nextPool = created.data ?? undefined;
      } else {
        const patched = await patchPool({
          poolId: existingPool.id,
          payload: {
            estimated_hours: draft.hours,
            hybrid_delivery_description: draft.description || null,
          },
        }).unwrap();
        nextPool = patched.data ?? undefined;
      }

      if (!nextPool) {
        return;
      }

      const existingPools = deliverable.service_type_pools ?? [];
      const hasExisting = existingPools.some((pool) => pool.id === nextPool.id);
      const nextPools = hasExisting
        ? existingPools.map((pool) =>
            pool.id === nextPool.id ? nextPool : pool,
          )
        : [...existingPools, nextPool];

      onPatched?.({
        service_type_pools: nextPools,
      });
      setPoolDrafts((current) => {
        if (!(serviceTypeId in current)) {
          return current;
        }
        const next = { ...current };
        delete next[serviceTypeId];
        return next;
      });
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <section className={styles.deliverableCard}>
      <div className={styles.deliverableCardHeader}>
        <SecondaryButton
          ariaLabel={isExpanded ? 'Collapse deliverable' : 'Expand deliverable'}
          variant="mini"
          onClick={() => setIsExpanded((current) => !current)}
        >
          <span
            className={`${styles.expandChevron} ${
              isExpanded ? styles.expandChevronOpen : ''
            }`}
          >
            <ChevronIcon direction="right" />
          </span>
        </SecondaryButton>
        <h3 className={styles.deliverableName}>{deliverable.name}</h3>

        <div className={styles.deliverableHeaderActions}>
          <DeliverableMetricsStrip
            jobId={jobId}
            deliverable={deliverable}
            readOnly={readOnly}
            showDueDate={jobKind !== 'retainer'}
            onPatched={onPatched}
            selectedDeliverableType={selectedDeliverableType}
            onDeliverableTypeChange={(nextType) => {
              void handleDeliverableTypeChange(nextType);
            }}
            hybridHoursLabel={`${formatHours(totalHybridHours)}h AI`}
            hybridSwitchTooltip={
              deliverable.has_hybrid_delivery
                ? 'Hybrid Delivery on • turn off'
                : 'Hybrid Delivery off • turn on to track AI contributions'
            }
            isHybridEnabled={Boolean(deliverable.has_hybrid_delivery)}
            isHybridSwitchDisabled={readOnly || isLoading}
            onHybridToggle={(checked) => {
              void handleHybridToggle(Boolean(checked));
            }}
          />
          <ChatContextButton
            target={{
              type: 'deliverable',
              id: deliverable.id,
              label: deliverable.name,
              sourceJobId: jobId,
            }}
            messageCount={deliverable.message_count}
            unreadCount={deliverable.unread_count}
            mentionCount={deliverable.mention_count}
          />
        </div>
      </div>

      {isExpanded ? (
        <div className={styles.deliverableExpanded}>
          {hybridRows.length ? (
            <div className={styles.deliverableMetersGroup}>
              <div className={styles.deliverableMetersRow}>
                {hybridRows.map((row) => {
                  const estimatedHours = row.pool?.estimated_hours ?? 0;
                  const assignedHours = row.pool?.assigned_hours ?? 0;
                  const actualHours = row.pool?.actual_hours ?? 0;
                  const apiPercentage = row.pool?.actual_hours_percentage;
                  const percentage =
                    typeof apiPercentage === 'number'
                      ? apiPercentage
                      : estimatedHours > 0
                        ? (actualHours / estimatedHours) * 100
                        : 0;
                  return (
                    <div
                      key={`${deliverable.id}:meter:${row.id}`}
                      className={styles.deliverableMeterCard}
                    >
                      <div className={styles.deliverableMeterHeader}>
                        <p className={styles.deliverableMeterLabel}>
                          {row.label}
                        </p>
                        <p className={styles.deliverableMeterNumbers}>
                          {formatHours(actualHours)}h /{' '}
                          {formatHours(estimatedHours)}h
                        </p>
                      </div>
                      <Meter
                        percentage={percentage}
                        actualValue={actualHours}
                        allocatedValue={assignedHours}
                        estimatedValue={estimatedHours}
                        ariaLabel={`${row.label} assignment`}
                      />
                      <p className={styles.deliverableMeterSummary}>
                        Assigned {formatHours(assignedHours)}h · Logged{' '}
                        {formatHours(actualHours)}h · Estimated{' '}
                        {formatHours(estimatedHours)}h
                      </p>
                    </div>
                  );
                })}
              </div>
              <div className={styles.deliverableMeterLegend}>
                <span>
                  <i className={styles.legendFill} /> fill = logged
                </span>
                <span>
                  <i className={styles.legendTick} /> tick = assigned
                </span>
                <span>track = estimated</span>
                <span>
                  <i className={styles.legendStripe} /> striped = assigned
                  beyond estimate
                </span>
              </div>
            </div>
          ) : null}

          <div className={styles.hybridPanel}>
            <div className={styles.hybridPanelHeader}>
              <SecondaryButton
                ariaLabel={
                  isHybridDetailsExpanded
                    ? 'Hide hybrid delivery details'
                    : 'Show hybrid delivery details'
                }
                variant="mini"
                onClick={() =>
                  setIsHybridDetailsExpanded((current) => !current)
                }
              >
                <span
                  className={`${styles.expandChevron} ${
                    isHybridDetailsExpanded ? styles.expandChevronOpen : ''
                  }`}
                >
                  <ChevronIcon direction="right" />
                </span>
              </SecondaryButton>
              <div className={styles.hybridPanelTitleGroup}>
                <p className={styles.detailTitle}>Hybrid Delivery</p>
                <p className={styles.detailHint}>
                  Estimate how much AI contributed to each type of work.
                </p>
              </div>
              <div className={styles.hybridPanelActions}>
                {deliverable.has_hybrid_delivery ? (
                  <Badge
                    tone="success"
                    size="lg"
                    label={`${formatHours(totalHybridHours)}h AI`}
                  />
                ) : null}
              </div>
            </div>

            {isHybridDetailsExpanded ? (
              deliverable.has_hybrid_delivery ? (
                <div className={styles.poolTable}>
                  <div className={styles.poolHeader}>
                    <span>Service Type</span>
                    <span>Description</span>
                    <span>AI Contribution (hrs)</span>
                  </div>

                  {hybridRows.map((row) => (
                    <div key={row.id} className={styles.poolRow}>
                      <span>{row.label}</span>
                      <SmartInput
                        type="text"
                        name={`pool-description-${deliverable.id}-${row.id}`}
                        label=""
                        value={getPoolDraft(row.id, row.pool).description}
                        readOnly={readOnly}
                        onChange={(nextValue) => {
                          setPoolDrafts((current) => ({
                            ...current,
                            [row.id]: {
                              description: nextValue,
                              hours:
                                current[row.id]?.hours ??
                                row.pool?.estimated_hours ??
                                0,
                            },
                          }));
                        }}
                        onBlur={() => {
                          void handleSavePool(row.id);
                        }}
                      />
                      <SmartInput
                        type="number"
                        name={`pool-hours-${deliverable.id}-${row.id}`}
                        label=""
                        value={getPoolDraft(row.id, row.pool).hours}
                        readOnly={readOnly}
                        min={0}
                        onChange={(nextValue) => {
                          setPoolDrafts((current) => ({
                            ...current,
                            [row.id]: {
                              description:
                                current[row.id]?.description ??
                                row.pool?.hybrid_delivery_description ??
                                '',
                              hours: Math.max(0, nextValue ?? 0),
                            },
                          }));
                        }}
                        onBlur={() => {
                          void handleSavePool(row.id);
                        }}
                      />
                    </div>
                  ))}

                  <div className={styles.poolTotalRow}>
                    <span>Total AI contribution</span>
                    <span>{formatHours(totalHybridHours)}h</span>
                  </div>
                </div>
              ) : (
                <p className={styles.hybridEmpty}>
                  No AI-assisted work recorded
                </p>
              )
            ) : null}
          </div>

          <DeliverableTasksTable
            jobId={jobId}
            deliverableId={deliverable.id}
            retainerCycleId={retainerCycleId}
            jobKind={jobKind}
            readOnly={readOnly}
            title={deliverable.name}
            availableServiceTypes={(deliverable.service_type_pools ?? [])
              .map((pool) => ({
                id: pool.service_type?.id ?? pool.service_type_id ?? '',
                label:
                  pool.service_type?.label ??
                  serviceTypeLabels.get(
                    pool.service_type?.id ?? pool.service_type_id ?? '',
                  ) ??
                  '',
              }))
              .filter((serviceType) => serviceType.id && serviceType.label)}
          />
        </div>
      ) : null}
    </section>
  );
}
